import { type FC, type PropsWithChildren, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import InformationPage, { type IErrorPageProps } from "./InformationPage.js";
import { type Configuration } from "@/graphql.ts";
import { type IFetchError, resetFetchError } from "@/redux/modules/errors.ts";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import {
  AUTHORISATION_EXCEPTION,
  INTERNAL_SERVER_ERROR,
  LICENSE_SEATS_EXCEPTION,
  MODULE_NOT_ENABLED_EXCEPTION,
  NOT_FOUND_EXCEPTION,
  PRIMARY_PARENT_TYPE_MISSING,
} from "@/settings/settings.js";

/**
 * Content of the app below the NavBar
 * Shows the page as defined in the routes config file,
 * or shows an error page e.g. NOT FOUND otherwise
 */
export const DataFetchingError: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const fetchError = useSelector<IAppState, IFetchError>((state) => state.errors.fetchError);
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);
  const dispatch = useDispatch<ReduxDispatch>();

  const getFetchErrorProperties = useCallback(
    (fetchError: IFetchError): IErrorPageProps => {
      switch (fetchError.type) {
        case NOT_FOUND_EXCEPTION:
          return {
            errorCode: 404,
            errorTitle: `${fetchError.message}`.toUpperCase(),
            children: (
              <span>
                The entity you are looking for could not be found.
                <br /> It may have been deleted or automatically purged.
              </span>
            ),
          };
        case AUTHORISATION_EXCEPTION:
          return {
            errorCode: 401,
            errorTitle: `${fetchError.message}`.toUpperCase(),
            children: "If you think this is an error, please contact the project administrator.",
          };
        case MODULE_NOT_ENABLED_EXCEPTION:
          return {
            errorCode: 403,
            errorTitle: `${fetchError.message}`.toUpperCase(),
            children: "Please verify your license or configuration.",
          };

        case INTERNAL_SERVER_ERROR:
          return {
            errorCode: 500,
            errorTitle: `${fetchError.message}`.toUpperCase(),
            children: "An internal server error occured, please report this issue to your administrator.",
          };
        case LICENSE_SEATS_EXCEPTION:
          return {
            errorTitle: `${fetchError.message}`.toUpperCase(),
            children: (
              <span>
                There are no more license seats available. Please try again later or ask your administrator to add
                additional seats.
              </span>
            ),
            showReloadLink: true,
          };
        case PRIMARY_PARENT_TYPE_MISSING:
          return {
            errorTitle: "Primary Parent Type Missing",
            children: (
              <div>
                <div>{fetchError.message}</div>
              </div>
            ),
            noGoBackLink: true,
          };
        default:
          throw Error("Unknown error type: " + fetchError.type);
      }
    },
    [configuration],
  );

  useEffect(() => {
    // we assume the error is bound to a specific route, so that we can navigate away from this error
    if (fetchError) {
      dispatch(resetFetchError());
    }
  }, [location.pathname]);

  return fetchError ? <InformationPage {...getFetchErrorProperties(fetchError)} /> : children;
};
